// AI generated
import type { Node, NodeOrigin, Rect, XYPosition } from '@xyflow/react'
import {
  boxToRect,
  getBoundsOfBoxes,
  getNodePositionWithOrigin,
  rectToBox,
} from '@xyflow/system'

export const getNodeSize = (node: Partial<Node>) => ({
  width: node.measured?.width ?? node.width ?? 0,
  height: node.measured?.height ?? node.height ?? 0,
})

/**
 * Turns a parent-relative position into canvas coordinates by walking the
 * `parentId` chain. A child's `position` is relative to its group, so anything
 * that compares two nodes has to bring them into this space first.
 */
export const toAbsolutePosition = (
  position: XYPosition,
  parentId: string | undefined,
  findNode: (id: string) => Node | undefined,
): XYPosition => {
  let { x, y } = position
  let currentId = parentId
  // guards against a parent cycle in a corrupted document
  const visited = new Set<string>()

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId)

    const parent = findNode(currentId)

    if (!parent) break

    x += parent.position.x
    y += parent.position.y
    currentId = parent.parentId
  }

  return { x, y }
}

const clamp = (value: number, max: number) => Math.max(0, Math.min(value, max))

/** Local position inside `groupNode`, clamped to `[0, group - node]`. */
export const getNodePositionInsideParent = (
  node: Partial<Node>,
  groupNode: Node,
) => {
  const position = node.position ?? { x: 0, y: 0 }
  const nodeSize = getNodeSize(node)
  const groupSize = getNodeSize(groupNode)

  return {
    x: clamp(
      position.x - groupNode.position.x,
      groupSize.width - nodeSize.width,
    ),
    y: clamp(
      position.y - groupNode.position.y,
      groupSize.height - nodeSize.height,
    ),
  }
}

export const getRelativeNodesBounds = (
  nodes: Node[],
  nodeOrigin: NodeOrigin = [0, 0],
): Rect => {
  if (nodes.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 }
  }

  const box = nodes.reduce(
    (currBox, node) => {
      const { x, y } = getNodePositionWithOrigin(node, nodeOrigin)
      return getBoundsOfBoxes(
        currBox,
        rectToBox({ x, y, ...getNodeSize(node) }),
      )
    },
    { x: Infinity, y: Infinity, x2: -Infinity, y2: -Infinity },
  )

  return boxToRect(box)
}
